/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Logger, LogService } from '@epg/angular-base-lib';
import {
  faArrowDownShortWide,
  faArrowUpShortWide,
  faCheck,
  faCodeBranch,
  faFilterSlash,
  faPenRuler,
  faPlus,
  faSearch,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { faSortDown } from '@fortawesome/pro-solid-svg-icons';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { toTitleCase } from '../../../app/shared/functions';
import { WorkflowModel } from '../../../models/workflow.model';
import { WorkflowService } from '../../../services/workflow.service';
import { DialogComponent } from '../../shared/dialog/dialog.component';
import { StandardListItemData } from '../../shared/standard-list-item/standard-list-item.component';
import { USER_INPUT_DEBOUNCE_TIME_IN_MS } from '../../shared/constants';

export enum SortCriteria {
  UPDATED_DATE,
  ALPHABETICAL
}
@Component({
  selector: 'app-workflow',
  templateUrl: './workflow.component.html',
  styleUrls: ['./workflow.component.scss'],
  standalone: false
})
export class WorkflowComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport!: CdkVirtualScrollViewport;
  @ViewChild('workflowCreationTemplate', { static: true })
  workflowCreationTemplate!: TemplateRef<any>;

  private destroy$ = new Subject<void>();
  private searchText$ = new Subject<string>();
  private cancelSubject = new Subject<void>();

  dialogTitle = $localize`:@@createWorkflow:Create Workflow`;

  // Icons
  faCodeBranch = faCodeBranch;
  faFilterSlash = faFilterSlash;
  faSearch = faSearch;
  faArrowUpShortWide = faArrowUpShortWide;
  faArrowDownShortWide = faArrowDownShortWide;
  faSortDown = faSortDown;
  faCheck = faCheck;
  faPenRuler = faPenRuler;
  faPlus = faPlus;
  faSpinnerThird = faSpinnerThird;

  // Data
  workflows: WorkflowModel[] = [];
  filteredWorkflows: WorkflowModel[] = [];
  combinedWorkflowItems: StandardListItemData[] = [];
  filteredWorkflowItems: StandardListItemData[] = [];
  selectedWorkflowId: string | null = null;
  isLoading = true;

  // Transform workflow to StandardListItemData
  transformWorkflowToItem(workflow: WorkflowModel): StandardListItemData {
    const workflowId = this.getWorkflowCombinedId(workflow);
    return {
      id: workflowId,
      name: workflow.name,
      routerLink: ['/home/workflow', workflowId],
      routerState: { workflow: workflow }
    };
  }

  private getWorkflowCombinedId(workflow: WorkflowModel): string {
    return workflow.combinedId ?? `${workflow._id ?? ''}_${workflow.designerId ?? ''}`;
  }

  // Compute a human-friendly version text from the item's badges
  getWorkflowVersionText(item: StandardListItemData): string {
    const workflowVersion = (item as any)?.routerState?.workflow?.version;
    if (workflowVersion > 0) {
      return $localize`:@@workflow.version_text:Version ${workflowVersion}`;
    }
    return $localize`:@@workflow.unpublished:unpublished`;
  }

  // Filters
  searchText = '';
  filters = {
    showPublished: false,
    showDrafts: false,
    showEditableOnly: false
  };

  // Sort
  isAscending = false;
  sortCriteria: SortCriteria = SortCriteria.UPDATED_DATE;
  SortCriteria = SortCriteria;

  get hasActiveFilters(): boolean {
    return Object.values(this.filters).some((value) => value === true);
  }

  private log: Logger;

  constructor(
    private workflowService: WorkflowService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    logService: LogService
  ) {
    this.log = logService.getLogger(this);
  }
  ngOnInit(): void {
    this.setupSearch();
    this.subscribeToCombinedWorkflows();

    // Keep selection in sync with URL (e.g. after publish navigation)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setSelectedWorkflowFromUrl();
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToCombinedWorkflows(): void {
    const applyWorkflows = (combinedWorkflows: WorkflowModel[]) => {
      this.workflows = combinedWorkflows;
      this.applyFilters();
      this.setSelectedWorkflowFromUrl();
      if (this.selectedWorkflowId) {
        setTimeout(() => this.scrollToSelectedWorkflow(), 0);
      }
      if (this.isLoading) {
        this.isLoading = false;
      }
    };

    // Initial load
    this.workflowService
      .getCombinedWorkflows()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: applyWorkflows,
        error: (error: any) => {
          this.log.error('Error getting combined workflows:', error);
          this.isLoading = false;
        }
      });

    // React to subsequent changes (publish/unpublish/delete/duplicate)
    this.workflowService.workflowsChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((combinedWorkflows: WorkflowModel[]) => {
        applyWorkflows(combinedWorkflows);
      });
  }

  private setSelectedWorkflowFromUrl(): void {
    // URL parsing to set selectedWorkflowId for scroll functionality
    const urlSegments = this.router.url.split('?')[0].split('/');
    const workflowIndex = urlSegments.indexOf('workflow');

    if (workflowIndex !== -1 && urlSegments.length > workflowIndex + 1) {
      this.selectedWorkflowId = urlSegments[workflowIndex + 1];
    } else {
      this.selectedWorkflowId = null;
    }
  }

  private setupSearch(): void {
    this.searchText$
      .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME_IN_MS), takeUntil(this.destroy$))
      .subscribe((searchText) => {
        this.searchText = searchText;
        this.applyFilters();
      });
  }

  onSearchChange(searchText: string): void {
    this.searchText$.next(searchText);
  }

  toggleFilter(filterName: keyof typeof this.filters): void {
    this.filters[filterName] = !this.filters[filterName];
    this.applyFilters();
  }

  clearFilters(): void {
    // Reset all filters to false
    Object.keys(this.filters).forEach((key) => {
      this.filters[key as keyof typeof this.filters] = false;
    });
    this.applyFilters();
  }

  applyFilters(): void {
    if (!this.workflows.length) {
      this.filteredWorkflows = [];
      return;
    }

    this.filteredWorkflows = this.workflows.filter((workflow) => {
      return this.filterBySearchText(workflow) && this.filterByActiveFilters(workflow);
    });

    this.sortItems();

    // Scroll to selected item if there is one
    if (
      this.selectedWorkflowId &&
      this.filteredWorkflows.some(
        (workflow) => this.getWorkflowCombinedId(workflow) === this.selectedWorkflowId
      )
    ) {
      setTimeout(() => {
        this.scrollToSelectedWorkflow();
      }, 0);
    }
  }

  private filterBySearchText(workflow: WorkflowModel): boolean {
    const searchText = this.searchText?.toLowerCase().trim() || '';
    return (
      !searchText ||
      workflow.name.toLowerCase().includes(searchText) ||
      (workflow.description?.toLowerCase().includes(searchText) ?? false)
    );
  }

  private filterByActiveFilters(workflow: WorkflowModel): boolean {
    if (!this.hasActiveFilters) {
      return true;
    }

    // Filter by draft status if active
    if (this.filters.showDrafts && !workflow.is_draft) {
      return false;
    }

    // // Filter by editable availability if active
    if (this.filters.showEditableOnly && !workflow.availableInDesigner) {
      return false;
    }

    // // Filter by published status if active
    if (this.filters.showPublished && !workflow.is_published) {
      return false;
    }

    return true;
  }

  trackByIdx(index: number, item: WorkflowModel): string {
    return item.combinedId || index.toString();
  }

  onWorkflowClick(item: StandardListItemData): void {
    // Set selected workflow
    this.selectedWorkflowId = item.id;

    // Navigate to the workflow detail
    if (item.routerLink) {
      const routerLink = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(routerLink, {
        state: item.routerState,
        queryParamsHandling: 'preserve'
      });
    }
  }

  isWorkflowSelected(workflow: WorkflowModel): boolean {
    return this.selectedWorkflowId === this.getWorkflowCombinedId(workflow);
  }

  // Public method to scroll to selected item (can be called from template or other components)
  scrollToSelected(): void {
    this.scrollToSelectedWorkflow();
  }

  private scrollToSelectedWorkflow(): void {
    if (!this.virtualScrollViewport || !this.selectedWorkflowId) {
      return;
    }

    const selectedIndex = this.filteredWorkflows.findIndex(
      (workflow) => workflow.combinedId === this.selectedWorkflowId
    );

    if (selectedIndex >= 0) {
      // Calculate the viewport height and item height to center the selected item
      const viewportElement = this.virtualScrollViewport.elementRef.nativeElement;
      const viewportHeight = viewportElement.clientHeight;
      const itemHeight = 64; // itemSize from template
      const itemsInView = Math.floor(viewportHeight / itemHeight);
      const offsetToCenter = Math.floor(itemsInView / 2);

      // Calculate the target index to scroll to (selected index minus offset to center it)
      const targetIndex = Math.max(0, selectedIndex - offsetToCenter);

      this.virtualScrollViewport.scrollToIndex(targetIndex, 'smooth');
    }
  }

  openWorkflowDialog() {
    this.dialog.open(DialogComponent, {
      data: {
        header: $localize`:@@createNewWorkflow:Create New Workflow`,
        template: this.workflowCreationTemplate,
        confirmButtonText: toTitleCase($localize`:@@create:Create`),
        cancelButtonText: toTitleCase($localize`:@@buttonCancel:CANCEL`),
        templateHandlesLayout: true,
        cancelStream: this.cancelSubject.asObservable()
      },
      width: '500px'
    });
  }

  closeWorkflowDialog() {
    this.cancelSubject.next();
  }

  sortItems(): void {
    this.filteredWorkflows = [
      ...this.filteredWorkflows.sort((a, b) => {
        if (this.sortCriteria === SortCriteria.UPDATED_DATE) {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return this.isAscending ? dateA - dateB : dateB - dateA;
        } else if (this.sortCriteria === SortCriteria.ALPHABETICAL) {
          return this.isAscending ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        }
        return 0;
      })
    ];
  }

  toggleSortOrder(): void {
    this.isAscending = !this.isAscending;
    this.sortItems();
  }

  changeSortCriteria(criteria: SortCriteria): void {
    this.sortCriteria = criteria;
    this.sortItems();
  }
}
